import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  inject,
  DestroyRef,
} from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import { saveAs } from 'file-saver';
import { BehaviorSubject, Observable, Subject, timer } from 'rxjs';
import { filter } from 'rxjs/operators';
import * as Sentry from '@sentry/browser';
import {
  GridOptions,
  GridApi,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  IServerSideGetRowsRequest,
  GridPreDestroyedEvent,
} from 'ag-grid-community';
import { CustomColDef } from '../../interfaces/report.interface';
import {
  Filter,
  Report,
  ReportGQL,
  RunReportAsyncGQL,
  FetchReportResultGQL,
  Config,
  RunReportGQL,
  FetchReportResultQuery,
} from '@app/generated/graphql';
import { EventBusService } from '@app/core/services/event-bus/event-bus.service';
import { Ag1GridLoadingOverlayComponent } from './ag1-grid-loading-overlay.component';
import { GridColumnService } from './services/grid-column.service';
import { DatePipe, AsyncPipe } from '@angular/common';
import { PollingService } from '@app/core/services/polling/polling.service';
import { Ag1BettabaseErrorComponent } from '../ag1-bettabase-error/ag1-bettabase-error.component';
import { Bind } from 'primeng/bind';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { AgGridAngular } from 'ag-grid-angular';
import { Ag1LoaderComponent } from '../ag1-loader/ag1-loader.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface GridConfig extends Config {
  gridOptions?: GridOptions<any>;
  gridColumns?: Record<string, any>;
  gridUI?: Record<string, any>;
  gridCustom?: Record<string, any>;
}

@Component({
  selector: 'ag1-grid',
  templateUrl: './ag1-grid.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Ag1BettabaseErrorComponent,
    Bind,
    Button,
    Tooltip,
    AngularSvgIconModule,
    AgGridAngular,
    Ag1LoaderComponent,
    AsyncPipe,
  ],
})
export class Ag1GridComponent implements OnInit, OnDestroy {
  private _destroyRef = inject(DestroyRef);
  private reportGQL = inject(ReportGQL);
  private runReportGQL = inject(RunReportGQL);
  private runReportAsyncGQL = inject(RunReportAsyncGQL);
  private fetchReportResultGQL = inject(FetchReportResultGQL);
  private eventBus = inject(EventBusService);
  private datePipe = inject(DatePipe);
  private gridColumnService = inject(GridColumnService);
  private pollingService = inject(PollingService);

  // subscription cleanup
  private destroy$ = new Subject<void>();

  @Input() reportId: any;

  isServerSide: boolean = true;
  SERVER_SIDE_ROW_COUNT: number = 100; // set to the default batch size for ag grid. If batch size is customised this needs to be updated

  // report group filters
  appliedReportGroupFilters: Filter[];
  // AgGrid filter model
  filterModel: Filter[];

  private gridApi: GridApi;
  private columnDefs: CustomColDef[];
  private rowData: any[];
  private dataSource: IServerSideDatasource;
  private dateTimeFormat = 'yyyy-MM-dd HH:mm';
  private dateFormat = 'yyyy-MM-dd';
  private timeZone = 'UTC';

  // docs: https://www.ag-grid.com/javascript-data-grid/grid-options/
  gridOptions: GridOptions<any>;

  // used to tell the template to load the grid
  isLoadedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );
  isLoaded$: Observable<boolean> = this.isLoadedSubject.asObservable();

  // used to set the rowData on the grid
  rowDataSubject: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  rowData$: Observable<any[]> = this.rowDataSubject.asObservable();

  // used to change download button text
  isDownloadingSubject = new BehaviorSubject<boolean>(false);
  isDownloading$ = this.isDownloadingSubject.asObservable();

  // loader
  loadingOverlayComponent: any = Ag1GridLoadingOverlayComponent;

  // default grid height
  componentHeight: string;
  // default no title
  componentTitle: string;
  closedToolPanel: boolean;
  uuid: string;
  gridConfig: GridConfig;

  sortReset: boolean = false;

  // error code
  errorName: string;
  // error message
  errorMessage: string;
  isError: boolean = false;

  private dataReceived = false;

  @Output() loaded = new EventEmitter<string>();

  constructor() {}

  ngOnInit(): void {
    this.eventBus
      .getChannel('appliedReportGroupFilters')
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter((filters) => Array.isArray(filters)),
      )
      .subscribe((appliedReportGroupFilters) => {
        this.appliedReportGroupFilters = appliedReportGroupFilters;
        this.getReportConfiguration();
      });

    this.eventBus
      .getChannel('RefetchReport')
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter((data: { id: string; status: string }) => !!data?.id),
      )
      .subscribe((data: { id: string; status: string }) => {
        if (this.uuid === data.id) {
          if (data.status === 'failed') {
            const error = new Error(
              'Please refresh the page or check your connection. If the problem persists, contact support.',
            );
            error.name = 'Report Subscription Error';
            this.handleError(error);
          } else {
            this.refetchReport(data.id);
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.isLoadedSubject.complete();
    this.rowDataSubject.complete();
    this.isDownloadingSubject.complete();

    if (this.gridApi) {
      this.gridApi.destroy();
      this.gridApi = null;
    }
  }

  handleError(status: Error, type?: string): void {
    if (!this.isError) {
      // Clear timeout subscription to prevent timeout state from overriding error state
      console.error(status);

      // Handle error
      this.errorName = status.name || 'An error occurred';
      this.errorMessage = status.message;
      this.isError = true; // slightly confusing naming here. This controlls rendering for both errors and normal communication
      this.isLoadedSubject.next(true);

      this.loaded.emit(this.reportId);

      if (type !== 'communication') {
        Sentry.captureException(status);
      }
      throw status; // we still throw an 'error' for a communication to stop any further processing
    }
  }

  resetGrid(): void {
    this.errorName = null;
    this.errorMessage = null;
    this.isError = false;
    this.isLoadedSubject.next(false);
    this.dataReceived = false;

    if (this.gridApi) {
      this.sortReset = true;
      this.gridApi.setFilterModel(null);
      this.gridApi.showLoadingOverlay();
    }
  }

  getReportConfiguration(): void {
    this.reportGQL
      .fetch({ variables: { reportId: parseInt(this.reportId, 10) } })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(
        (res) => {
          try {
            this.resetGrid();

            const report: Report = res.data.report;
            this.gridConfig = this.getGridConfig(report.configs);
            this.componentTitle = this.gridConfig?.gridCustom?.componentTitle;

            // Set grid options and apply configurations
            this.configureOptions(this.gridConfig.gridOptions);
            this.configureRendering();
            this.configureUI(this.gridConfig);

            this.initializeGrid();
          } catch (error) {
            this.handleError(error);
          }
        },
        (error) => {
          this.handleError(error);
        },
      );
  }

  initializeGrid(): void {
    try {
      if (this.isServerSide) {
        this.fetchInitialDataForColumns(parseInt(this.reportId, 10));
      } else {
        this.fetchDataBulkAsync(parseInt(this.reportId, 10));
      }
    } catch (error) {
      this.handleError(error);
    }
  }

  private fetchInitialDataForColumns(reportId: number): void {
    this.dataSource = this.getServerSideDatasource(reportId);

    // Pre-build columns from config using an empty placeholder row so column headers
    // are visible immediately while the data query runs. Column type detection
    // (number/date formatters) is refined in getRows() on first real data arrival.
    if (this.gridConfig?.gridColumns) {
      this.configureColumns(this.gridConfig.gridColumns, [{}]);
    }

    if (this.gridApi) {
      this.gridApi.setGridOption('columnDefs', this.columnDefs);
      this.gridApi.setGridOption('serverSideDatasource', this.dataSource);
    }

    // Show grid
    this.isLoadedSubject.next(true);
  }

  fetchDataBulkAsync(reportId: number) {
    this.uuid = uuidv4();
    const variables = {
      reportId,
      requestUuid: this.uuid,
      reportFilters: this.appliedReportGroupFilters,
    };

    this.runReportAsyncGQL
      .fetch({ variables: variables, fetchPolicy: 'network-only' })
      .subscribe(
        () => {
          // Start polling after initial request
          timer(200)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(() => {
              if (!this.dataReceived) {
                this.startPolling();
              }
            });
        },
        (err) => {
          console.error('Failed to initiate bulk fetch:', err);
        },
      );
  }

  private startPolling(): void {
    this.pollingService.startPolling(
      // Poll function
      () => this.refetchReport(this.uuid),
      // Stop condition
      () => this.dataReceived,
      // Destroy subject
      this.destroy$,
    );
  }

  refetchReport(id: string): void {
    // Only proceed if this is our results
    if (this.uuid !== id) {
      return;
    }

    this.fetchReportResultGQL
      .fetch({ variables: { requestUuid: id }, fetchPolicy: 'network-only' })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(
        (res) => {
          if (this.isDownloadingSubject.getValue()) {
            this.exportAsCsv(res.data);
          } else {
            this.updateGridData(res.data);
          }
        },
        (error) => {
          this.handleError(error);
        },
      );
  }

  updateGridData(data: FetchReportResultQuery) {
    try {
      const parsedReportData = JSON.parse(data.fetchReportResult);
      this.rowData = parsedReportData?.result;
      if (this.rowData && this.rowData.length > 0) {
        // Set flag when data is received to stop polling
        this.dataReceived = true;
        this.configureColumns(this.gridConfig.gridColumns, this.rowData);

        // Mark grid as ready to set column definitions and load data into the grid
        this.isLoadedSubject.next(true);
        this.rowDataSubject.next(this.rowData);
      } else {
        const error = new Error(
          'Try adjusting your filters to broaden the search.',
        );
        error.name = 'No data to display';
        this.handleError(error, 'communication');
      }
    } catch (error) {
      this.handleError(error);
    }
  }

  exportCsvButton() {
    this.isDownloadingSubject.next(true);
    this.fetchDataBulkAsync(parseInt(this.reportId, 10));
  }

  // custom export as CSV
  exportAsCsv(data: FetchReportResultQuery) {
    try {
      const parsedReportData = JSON.parse(data.fetchReportResult);
      const reportData = parsedReportData?.result;
      if (reportData && reportData.length > 0) {
        // stop polling and change flag to no longer indicate we're download a CSV
        this.dataReceived = true;
        this.isDownloadingSubject.next(false);

        const csvData = this.convertToCsv(reportData);
        const blob = new Blob([csvData], {
          type: 'text/csv;charset=utf-8;',
        });
        saveAs(blob, `${this.componentTitle}_all.csv`);
      }
    } catch (error) {
      this.isDownloadingSubject.next(false);
      this.dataReceived = true;
      this.handleError(error); // Call outer error handler explicitly
    }
  }

  getServerSideDatasource(reportId: number): IServerSideDatasource {
    let isFirstLoad = true;

    return {
      getRows: (params: IServerSideGetRowsParams) => {
        // changing filters/ columns/ grouping etc will result in new requests to the server
        const gridRequest: IServerSideGetRowsRequest = params.request;
        gridRequest.startRow = gridRequest.startRow ?? 0;
        gridRequest.endRow = gridRequest.endRow ?? this.SERVER_SIDE_ROW_COUNT;

        // this is a hack to ensure sort is reset when
        // e.g. navigating to another QC report
        // this might cause issues with other SSRM grids
        if (this.sortReset) {
          gridRequest.sortModel = [];
          this.sortReset = false;
        }

        if (this.gridApi) {
          const filterModel = this.gridApi.getFilterModel();

          if (filterModel && Object.keys(filterModel).length > 0) {
            gridRequest.filterModel = Object.keys(filterModel).map(
              (fieldKey) => {
                return {
                  field: fieldKey, // e.g. commodity
                  // TODO: https://agrigate.atlassian.net/browse/DATA-2546
                  // TODO: https://agrigate.atlassian.net/browse/DATA-2547
                  fieldType: 'text', // Assume text data types for all columns for now
                  filterType: 'input',
                  condition: filterModel[fieldKey].type, //e.g. contains
                  values: [filterModel[fieldKey].filter],
                } as Filter;
              },
            );
          } else {
            gridRequest.filterModel = [];
          }
        }

        const variables = {
          reportId,
          gridRequest: gridRequest as any,
          reportFilters: this.appliedReportGroupFilters,
        };

        this.runReportGQL
          .fetch({ variables: variables })
          .pipe(takeUntilDestroyed(this._destroyRef))
          .subscribe(
            (res) => {
              const rowData = JSON.parse(res.data.runReport);

              if (!rowData) {
                params.fail();
                return;
              }

              // Deliver data to AG Grid before updating columnDefs. In AG Grid v36,
              // calling setGridOption('columnDefs') while a getRows() is still pending
              // resets the SSRM and invalidates the pending params.success() call,
              // causing the grid to appear blank indefinitely.
              let rowCount = -1;
              if (rowData.length <= this.SERVER_SIDE_ROW_COUNT) {
                rowCount = gridRequest.startRow + rowData.length;
              }

              params.success({ rowData, rowCount });
              this.gridApi?.hideOverlay();

              // Refine column type detection (number/date formatters) on first real data load.
              // Doing this after params.success() avoids triggering an SSRM reset in AG Grid v36.
              if (isFirstLoad && rowData.length > 0) {
                this.configureColumns(this.gridConfig.gridColumns, rowData);
                this.gridApi?.setGridOption('columnDefs', this.columnDefs);
                isFirstLoad = false;
              }
            },
            (error) => {
              console.warn('err', error);
              params.fail();
            },
          );
      },
    };
  }

  configureOptions(gridOptions: GridOptions) {
    this.gridOptions = {
      ...this.getDefaultGridOptions(),
      ...gridOptions,
    };
    // prevent memory leak by removing API reference when grid is destroyed
    this.gridOptions.onGridPreDestroyed = (event: GridPreDestroyedEvent) => {
      this.gridApi = null;
    };
  }

  configureRendering() {
    this.isServerSide = this.gridOptions?.pagination ?? this.isServerSide;

    // Configure the grid for server-side row model
    if (this.isServerSide) {
      this.gridOptions = {
        ...this.gridOptions,
        rowModelType: 'serverSide',
      };
    }
  }

  configureUI(gridConfig: GridConfig) {
    const height = gridConfig?.gridCustom?.componentHeight;
    this.componentHeight = height ?? '400px';
    this.componentTitle = gridConfig?.gridCustom?.componentTitle;
    this.closedToolPanel = gridConfig?.gridCustom?.closedToolPanel;
  }

  configureColumns(gridColumns: Record<string, any>, rowData) {
    const addFilters = this.gridConfig?.gridCustom?.addFilters ?? false;
    this.columnDefs = this.gridColumnService.configureColumns(
      gridColumns ?? [],
      rowData,
      addFilters,
    );

    // Apply conditional formatting
    this.columnDefs.forEach((colDef) => {
      if (colDef.conditionalFormatting) {
        colDef.cellStyle = (params) => {
          return this.applyConditionalFormatting(
            params.value,
            colDef.conditionalFormatting,
          );
        };
      }
    });
  }

  applyConditionalFormatting(
    value: any,
    rules: { condition: string; color: string }[],
  ) {
    for (const rule of rules) {
      try {
        const conditionFn = new Function('value', `return ${rule.condition};`);
        if (conditionFn(value)) {
          return { backgroundColor: rule.color };
        }
      } catch (error) {
        console.error(`Invalid condition: ${rule.condition}`, error);
      }
    }
    return null; // Default style
  }

  // docs: https://www.ag-grid.com/javascript-data-grid/grid-options/
  getDefaultGridOptions(): GridOptions {
    return {
      theme: 'legacy',
      // data
      defaultColDef: {
        flex: 1, // cols will take up the available space
        minWidth: 150,
        resizable: true,
        sortable: true,
        enableRowGroup: false,
        rowGroup: false,
        editable: false,
        hide: false,
      },
      // Enable virtual columns (data can load before columns are visible). We don't want data to only load when scrolling to the right on a table. It causes issues where data sometimes doesn't load.
      suppressColumnVirtualisation: false,
      pagination: true,
      paginationAutoPageSize: true,
      pivotMode: false,
      animateRows: true,
      enableCharts: false,
      suppressAggFuncInHeader: true,
      // showTotals: false,
      sideBar: {
        toolPanels: [
          {
            id: 'columns',
            labelDefault: 'Columns',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
              suppressRowGroups: true,
              suppressValues: true,
              suppressPivots: true,
              suppressPivotMode: true,
              suppressColumnFilter: true,
              suppressColumnSelectAll: true,
              suppressColumnExpandAll: true,
            },
          },
        ],
        defaultToolPanel: 'columns',
      },
    };
  }

  removeOverlay() {
    // try remove grid overlay every 50ms
    // if the gridapi is not ready
    // catch the error and try again
    try {
      this.gridApi.hideOverlay();
    } catch (e) {
      setTimeout(() => {
        this.removeOverlay();
      }, 50);
    }
  }

  onGridReady({ api }: { api: GridApi }) {
    this.gridApi = api;
    if (this.columnDefs) {
      this.gridApi.setGridOption('columnDefs', this.columnDefs);
    }

    this.gridApi.addEventListener('firstDataRendered', () => {
      this.loaded.emit(this.reportId);
    });

    // Set the datasource if the grid is server-side
    if (this.isServerSide) {
      this.gridApi.setGridOption('serverSideDatasource', this.dataSource);
    }

    if (this.closedToolPanel) {
      this.closeToolPanel();
    }
  }

  closeToolPanel() {
    this.gridApi.closeToolPanel();
  }

  // Utility function to parse JSON safely
  parseJSON(value: string, name: string) {
    try {
      return JSON.parse(value);
    } catch {
      const error = new Error(`Invalid JSON value for ${name}: ${value}`);
      error.name = 'Invalid JSON';
      this.handleError(error);
    }
  }

  // Extract report configuration
  getGridConfig(configs: Config[]): GridConfig {
    return configs.reduce((obj, item) => {
      obj[item.name] = this.parseJSON(item.value, item.name);
      return obj;
    }, {});
  }

  // Utility function to convert custom serialized data to CSV
  convertToCsv(data: any[]): string {
    if (!data || data.length === 0) return '';

    const csvRows = [];
    const headers = Object.keys(data[0]);
    csvRows.push(headers.join(','));

    for (const row of data) {
      const values = headers.map((header) => {
        const value = row[header];

        // Check if value is a custom timestamp object
        if (value && typeof value === 'object' && value['^t']) {
          return this.datePipe.transform(
            value['^t'] * 1000,
            this.dateTimeFormat,
            this.timeZone,
          );
        }
        // Check if value is a custom date object
        else if (value && typeof value === 'object' && value['^O'] === 'Date') {
          return this.datePipe.transform(
            new Date(value.year, value.month - 1, value.day),
            this.dateFormat,
            this.timeZone,
          );
        }
        // Handle arrays containing objects (like calculation_results)
        else if (
          Array.isArray(value) &&
          value.some(
            (item) => item && typeof item === 'object' && !Array.isArray(item),
          )
        ) {
          try {
            // Format the JSON with proper escaping for CSV
            const jsonString = JSON.stringify(value)
              .replace(/"/g, '""')
              .replace(/\n/g, ' ');
            return `"${jsonString}"`;
          } catch (e) {
            return `"${String(value).replace(/"/g, '""')}"`;
          }
        }

        // Handle arrays: Convert array to a comma-separated string wrapped in []
        else if (Array.isArray(value)) {
          return `"[${value.join(',')}]"`;
        }

        // Handle numbers and booleans without quotes
        else if (typeof value === 'number' || typeof value === 'boolean') {
          return value; // No quotes for numbers or booleans
        }
        // Handle strings: Wrap strings in quotes and escape any internal quotes
        else if (typeof value === 'string') {
          return `"${value.replace(/"/g, '""')}"`;
        }
        // Handle undefined or null values
        else {
          return ''; // Empty field for undefined or null values
        }
      });

      csvRows.push(values.join(','));
    }

    return csvRows.join('\n');
  }
}
